import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService, UserService } from '../../../core/services';
import { OauthProvider } from '../../../oauth/providers';
import { CompanyDetailService } from '../../../company-detail/services/companies-detail.service';
import { getErrorMessage } from '../../../shared/helpers';
import { AccountAttributesEnum, FeatureFlagsEnum } from '../../../shared/enums';
import { ReConnectSettingComponent } from '../re-connect-setting/re-connect-setting.component';
import { SiModalModule } from '../si-modal/si-modal.module';
import { DuplicateResolutionRadioGroupModule } from '../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';
import {
  CommonUtilService,
  FeatureFlagService,
  RequestBodyService,
  RoDs,
  SocketExportService,
} from '../../../shared/services';
import { SalesforceService } from '../../../shared/services/crm/salesforce.service';
import { SalesforceApiService } from '../../../shared/services/api';
import { CompanyService } from '../../../results/companies/services';
import { CompanyinboxService } from '../../../results/inboxintel/companies/services/companyinbox.service';
import { InboxintelService } from '../../../results/inboxintel/services/inboxintel.service';

@UntilDestroy()
@Component({
  selector: 'app-company-sf-export-component',
  templateUrl: './company-sf-export-component.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SiModalModule,
    DuplicateResolutionRadioGroupModule,
  ],
})
export class CompanySfExportComponentComponent implements OnInit {
  pages = {
    loading: 0,
    duplicates: 1,
    export: 2,
    exportFinished: 3,
    exportEmpty: 4,
    exportFailed: 5,
    requiresReauth: 6
  };
  currentPage: number = this.pages.loading;
  initialPage: number = this.currentPage;

  showPreviousButton: boolean = false;
  showNextButton: boolean = false;
  showCloseButton: boolean = false;
  showCancelButton: boolean = true;
  showRetryButton: boolean = false;

  isBusy: boolean = false;
  busyLabel = 'Loading ...';
  busySublabel = '';
  exportError = '';
  duplicateResolution = 'update';
  generalSettings;
  companyData = [];
  companyScoreLists: number[] = [];
  duplicateCompanyMatchIds = [];
  duplicateCompanyCount = 0;
  exportedCompanyCount = 0;
  failedCompany = [];
  isExportButton = false;
  humanVerifiedCompanies = [];
  machineVerifiedCompanies = [];
  unmatchedCompanies = [];
  exportFrom;
  isSFLoggedin = false;
  isSalesForceComponentOpened: boolean = true;
  constructor(
    private commonUtilService: CommonUtilService,
    private salesforceService: SalesforceService,
    private salesforceApiService: SalesforceApiService,
    private authenticationService: AuthenticationService,
    private readonly matDialog: MatDialog,
    private readonly matDialogRef: MatDialogRef<CompanySfExportComponentComponent>,
    private requestBodyService: RequestBodyService,
    private toastr: ToastrService,
    private companyInboxService: CompanyinboxService,
    private inboxintelService: InboxintelService,
    private companyService: CompanyService,
    private rodsService: RoDs,
    private companyDetailService: CompanyDetailService,
    private userService: UserService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly socketExportService: SocketExportService,
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
  ) {
    this.companyScoreLists = this.data['companyScoreLists'] || [];

    if (this.data['company']) {
      this.companyData = this.data['company'];
      this.exportFrom = this.data['fromExport'];
      if (this.exportFrom === 'fromInboxIntelCompany' || this.data['fromExport'] === 'fromInboxIntelCompany') {
        this.isExportButton = true;
        this.exportFrom = 'fromInboxIntelCompany';
        this.companyData.map((item) => {
          if (item['searchResult'] && item['searchResult'].company_id) {
            if (item['searchResult'] && item['searchResult'].verified) {
              this.humanVerifiedCompanies.push(item);
            } else {
              this.machineVerifiedCompanies.push(item);
            }
          } else {
            this.unmatchedCompanies.push(item);
          }
        });
      }
    }
    if (this.salesforceService.isReconnectCompany || this.salesforceService.isFromInboxIntelCompany) {
      this.exportFrom = 'fromInboxIntelCompany';
      this.isExportButton = false;
      this.exportInboxintel();
      this.salesforceService.isReconnectCompany = false;
    }
  }

  ngOnInit() {
    if (this.isExportButton === false && this.exportFrom !== 'fromInboxIntelCompany') {
      this.initializeFirstStep();
    }
  }

  initializeFirstStep() {
    this.currentPage = this.pages.loading;
    this.initialPage = this.currentPage;
    this.salesforceApiService.validateMapping().pipe(untilDestroyed(this)).subscribe((mappingData) => {
      if (Object.values(mappingData).every(item => item === true)) {
        this.getSettings();
      } else {
        const sectionsInError = [];
        if (!mappingData.accountFields) {
          sectionsInError.push('Company Mappings');
        }
        if (this.userService.hasAccess(AccountAttributesEnum.TECHEXPORTS) && !mappingData.technoAccountFields) {
          sectionsInError.push('Technographics Mappings');
        }
        if (sectionsInError.length > 0) {
          sectionsInError.forEach(section => {
            this.commonUtilService.showToastWithError(
              `One or more fields in your ${section} are no longer available. Update your ${section}.`
            );
          });
        }
        this.matDialogRef.close();
      }
    }, error => {
      this.salesforceService.isFromInboxIntel = false;
      this.salesforceApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
        this.initializeFirstStep();
      }, refreshError => {
        this.errorRefreshToken();
      });
    });
  }

  next() {
    this.currentPage++;
    this.executeCurrentStep();
  }

  previous() {
    if (this.currentPage === this.pages.exportEmpty) {
      this.currentPage = this.pages.duplicates;
    } else {
      this.currentPage = this.currentPage--;
    }
    this.executeCurrentStep();
  }

  errorRefreshToken() {
    if (this.isSalesForceComponentOpened) {
      this.onClose();
      const modalConfig: MatDialogConfig = {
        panelClass: 'modal-lg',
        data: {
          provider: OauthProvider.SalesforceCompany,
        }
      };
      if (this.data['company']) {
        if (this.exportFrom === 'fromInboxIntelCompany') {
          modalConfig.data['match_ids'] = this.companyData;
          modalConfig.data['ifFromList'] = 'fromInboxIntelCompany';

        } else {
          modalConfig.data['match_ids'] = this.companyData;
        }
      }
      this.matDialog.open(ReConnectSettingComponent, modalConfig);
    }
  }

  executeCurrentStep() {
    if (this.currentPage === this.initialPage) {
      this.showPreviousButton = false;
      this.showCloseButton = false;
    } else if (this.currentPage === this.pages.duplicates) {
      this.showPreviousButton = false;
      this.showNextButton = true;
      this.showCloseButton = false;
    } else if (this.currentPage === this.pages.export) {
      this.showPreviousButton = true;
      this.showNextButton = true;
      this.showRetryButton = false;
      this.showCloseButton = false;
      this.export();
    } else if (this.currentPage === this.pages.exportEmpty) {
      this.showCancelButton = false;
      this.showPreviousButton = true;
      this.showNextButton = false;
      this.showCloseButton = true;
    } else if (this.currentPage === this.pages.exportFinished) {
      this.showCancelButton = false;
      this.showPreviousButton = false;
      this.showNextButton = false;
      this.showCloseButton = true;
    }
  }

  getSettings() {
    this.salesforceApiService.getSettings().pipe(untilDestroyed(this)).subscribe((response) => {
      this.generalSettings = response.general;
      if (this.exportFrom === 'fromInboxIntelCompany') {
        // this.isExportButton = true;
        this.validateDuplicates();
      } else {
        this.validateDuplicates();
        this.isExportButton = false;
      }
    }, (error) => {
      if (error.status === 404 && error.error?.message) {
        this.commonUtilService.showToastWithError(getErrorMessage(error));
        this.onClose();
        return;
      }
      this.salesforceApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
        this.ngOnInit();
      }, (refreshError) => {
        if (refreshError.error['errors'].length > 0 && refreshError.error['errors'][0].code === 'invalid_grant') {
          this.errorRefreshToken();
        }
      });
    });
  }

  exportInboxintel() {
    const companyIds = [];
    const machineCompanyIds = [];
    const unmatchedCompanyIds = [];
    this.isExportButton = false;
    this.companyData.map((item) => {
      if(item && item['searchResult']){
        if (item && item['searchResult']['company_id']) {
          if (item['searchResult'] && item['searchResult'].verified) {
            companyIds.push(item['searchResult'].company_id.toString());
          } else {
            machineCompanyIds.push(item['searchResult'].company_id.toString());
          }
        } else {
          if(item.inboxIntelCompanyId){
            unmatchedCompanyIds.push(item.inboxIntelCompanyId.toString());
          }
        }
      }else{
        if (item && item['companyId']) {
          if (item && item.verified) {
            companyIds.push(item.companyId.toString());
          } else {
            machineCompanyIds.push(item.companyId.toString());
          }
        } else {
          if(item.inboxIntelCompanyId){
            unmatchedCompanyIds.push(item.inboxIntelCompanyId.toString());
          }
        }
      }

    });
    if (this.authenticationService.hasEmail('sfEmail')) {
      this.isSFLoggedin = true;
    }
    if (this.isSFLoggedin) {
      this.salesforceService.isFromInboxIntelCompany = false;
      if (companyIds.length > 0) {
        this.initializeFirstStep();
      }
      if (machineCompanyIds.length > 0 || unmatchedCompanyIds.length > 0) {
        if (this.commonUtilService.hasRODCreditsRemaining) {
          this.onRequestVerification(this.machineVerifiedCompanies, this.unmatchedCompanies);
        }
      }
    } else {
      if (!this.isSFLoggedin && ((machineCompanyIds.length > 0 && companyIds.length === 0) || (unmatchedCompanyIds.length > 0 && companyIds.length === 0))) {
        if (this.commonUtilService.hasRODCreditsRemaining) {
          this.onRequestVerification(this.machineVerifiedCompanies, this.unmatchedCompanies);
        }
      } else {
        this.commonUtilService.openOauthCRM(OauthProvider.SalesforceCompany, this.companyData, 'fromInboxIntelCompany');
        this.onClose();
      }
    }
  }

  validateDuplicates() {
    this.next();
    this.busyLabel = 'Validating Duplicates ...';
    this.busySublabel = 'Depending on the number of company, this could take a few minutes.';
    this.isBusy = true;
    const companyIds = [];
    this.companyData.map((item) => {
      if (this.data['fromExport'] === 'fromInboxIntelCompany') {
        if(item && item['searchResult']){
          if (item && item['searchResult']['company_id']) {
            if (item['searchResult'] && item['searchResult'].verified) {
              companyIds.push(item['searchResult'].company_id.toString());
            }
          }
        }else{
          if (item && item['companyId']) {
            if (item && item.verified) {
              companyIds.push(item.companyId.toString());
            }
          }
        }

      } else {
        if (typeof item === 'object') {
          companyIds.push(item.companyId.toString());
        } else {
          return companyIds.push(item);
        }
      }
    });
    const requestBody = {
      company_ids: companyIds,
      verified: this.data['verified'],
      is_international: this.data['international'],
      company_score_lists: this.companyScoreLists
    };
    this.salesforceApiService.validateCompaniesDuplicates(requestBody).pipe(untilDestroyed(this)).subscribe((resp: any) => {
      resp.accounts.forEach((item) => {
        if (item['duplicate'] === true) {
          this.duplicateCompanyMatchIds.push(item);
        }
      });
      this.duplicateCompanyCount = this.duplicateCompanyMatchIds.length;

      if (this.duplicateCompanyCount === 0) {
        this.next();
        this.isExportButton = false;
      } else if (this.generalSettings.sfDuplicateResolution !== 'prompt') {
        this.duplicateResolution = this.generalSettings.sfDuplicateResolution;
        this.next();
        this.isExportButton = false;
      } else {
        this.isBusy = false;
        this.isExportButton = false;
      }
    }, (error) => {
      if (error.status === 403) {
        this.commonUtilService.showToastWithError(getErrorMessage(error));
        this.onClose();
      }
      this.errorRefreshToken();
    });
  }

  export() {
    const toBeExported = [];
    const machineCompanyIds = [];
    const unmatchedCompanyIds = [];
    this.companyData.map((item) => {
      if (this.data['fromExport'] === 'fromInboxIntelCompany') {
        if(item && item['searchResult']){
          if (item && item['searchResult']['company_id']) {
            if (item['searchResult'] && item['searchResult'].verified) {
              toBeExported.push(item['searchResult'].company_id.toString());
            } else {
              machineCompanyIds.push(item['searchResult'].company_id.toString());
            }
          } else {
            if(item.inboxIntelCompanyId){
              unmatchedCompanyIds.push(item.inboxIntelCompanyId.toString());
            }
          }
        }else{
          if (item && item['companyId']) {
            if (item && item.verified) {
              toBeExported.push(item.companyId.toString());
            } else {
              machineCompanyIds.push(item.companyId.toString());
            }
          } else {
            if(item.inboxIntelCompanyId){
              unmatchedCompanyIds.push(item.inboxIntelCompanyId.toString());
            }
          }
        }
      } else {
        if (typeof item === 'object') {
          toBeExported.push(item.companyId.toString());
        } else {
          return toBeExported.push(item.toString());
        }
      }
    });
    // Skip all duplicates
    if (this.duplicateCompanyMatchIds && this.duplicateResolution === 'skip') {
      this.duplicateCompanyMatchIds.forEach((item) => {
        this.commonUtilService.removeMatchId(toBeExported, item, 'company');
      });
    }

    if (toBeExported.length === 0) {
      this.isBusy = false;
      this.currentPage = this.pages.exportEmpty;
      this.executeCurrentStep();
    } else {
      this.exportedCompanyCount = toBeExported.length;
      this.busyLabel = 'Exporting ...';
      this.busySublabel = 'Depending on the number of companies, this could take a few minutes.';
      this.isBusy = true;
      const requestBody = {
        company_ids: toBeExported,
        verified: this.data['verified'],
        is_international: this.data['international'],
        companyScoreLists: this.companyScoreLists,
        duplicateResolution: this.duplicateResolution === 'skip' ? '' : this.duplicateResolution,
      };

      this.exportCompanies(requestBody).pipe(untilDestroyed(this)).subscribe((response) => {
        this.failedCompany = response;
        // Skip all failed company
        if (Array.isArray(this.failedCompany) && this.failedCompany.length > 0) {
          toBeExported.forEach((id) => {
            this.failedCompany.forEach((failedCompay) => {
              if (failedCompay.company_id === id) {
                this.commonUtilService.removeMatchId(toBeExported, id, 'company');
              }
            });
          });
        }
        if (this.data['fromExport'] === 'fromInboxIntelCompany') {
          this.isExportButton = false;
          const inboxIntelCompanyIds = this.companyData.map((item) => {
            if (typeof item === 'object' && item.inboxIntelCompanyIds) {
              return item.inboxIntelCompanyIds.toString();
            }
          });
          this.companyInboxService.exportToSalesforce(inboxIntelCompanyIds).subscribe((res) => {
            if (res['response'] === 'Success.') {
              this.inboxintelService.getStats();
            }
          });
        }
        if (toBeExported.length > 0) {
          this.authenticationService.updateLoggedUserData();
          const exportedIds = toBeExported.map((item) => parseInt(item, 10));
          this.requestBodyService.changeCompanyRevealMatchesID({ 'type': 'exportCSV', 'data': exportedIds });
        }
        this.isBusy = false;
        this.next();
        this.isExportButton = false;
      }, (error) => {
        const errMsg = getErrorMessage(error, true);
        if (errMsg) {
          this.exportError = errMsg;
          this.isExportButton = false;
          if (error.status === 402 || error.status === 406) {
            this.showRetryButton = false;
            this.showCloseButton = false;
            this.showCancelButton = true;
            this.isExportButton = false;
          } else {
            this.showRetryButton = true;
            this.isExportButton = false;
          }
        } else {
          this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
          this.isExportButton = false;
        }
        this.currentPage = this.pages.exportFailed;
        this.showNextButton = false;
        this.showPreviousButton = false;
        this.isBusy = false;
        this.isExportButton = false;
      });
    }
  }

  retryExport() {
    this.currentPage = this.pages.export;
    this.executeCurrentStep();
  }

  onClose(): void {
    this.isSalesForceComponentOpened = false;
    this.matDialogRef.close();
  }

  onRequestVerification(machineCompanyIds: any[], unmatchedCompanyIds: any[]) {
    if (this.commonUtilService.hasRODCreditsRemaining) {
      const selectedRequestCompanies = [];
      const notRequestedCompanies = [];
      const notRequestedUnMatchedCompanies = [];

      machineCompanyIds.forEach((item) => {
        const requestedData = this.companyService.getRequestResearchData()['company_ids'].find((companyId) => companyId === parseInt(item['searchResult'].company_id, 10));
        if (!requestedData) {
          notRequestedCompanies.push(item);

          selectedRequestCompanies.push({
            'company_id': item['searchResult'].company_id ? item['searchResult'].company_id : item.inboxIntelCompanyId,
            'company_name': item['searchResult'].primary_name ? item['searchResult'].primary_name : item.name,
            'company_domain': item['searchResult'].primary_domain ? item['searchResult'].primary_domain : item.domain
          });
        }
      });
      unmatchedCompanyIds.forEach((item) => {
        const requestedData = this.companyService.getRequestResearchData()['company_unmatched_ids'].find((companyId) => companyId === item.inboxIntelCompanyId);
        if (!requestedData) {
          notRequestedUnMatchedCompanies.push(item);

          selectedRequestCompanies.push({
            'company_id': item['searchResult'].company_id ? item['searchResult'].company_id : item.inboxIntelCompanyId,
            'company_name': item['searchResult'].primary_name ? item['searchResult'].primary_name : item.name,
            'company_domain': item['searchResult'].primary_domain ? item['searchResult'].primary_domain : item.domain
          });
        }
      });

      const requestBody = { companies: selectedRequestCompanies };

      if (notRequestedCompanies.length > 0 || notRequestedUnMatchedCompanies.length > 0) {
        this.companyDetailService.requestVerifyCompanyInboxintel(requestBody).subscribe(async (res) => {
          if (res.Status === 'Success') {
            notRequestedCompanies.forEach((companyId: any) => {
              const companyInfo = this.companyInboxService.resultPage.resultData.find((company) => company.searchResult.company_id === parseInt(companyId['searchResult'].company_id, 10));
              if (companyInfo) {
                this.companyService.updateRequestResearchData(companyId['searchResult'].company_id, false);
              }
            });
            notRequestedUnMatchedCompanies.forEach((companyId: any) => {
              const companyInfo = this.companyInboxService.resultPage.resultData.find((company) => company.inboxIntelCompanyId === companyId.inboxIntelCompanyId);
              if (companyInfo) {
                this.companyService.updateRequestResearchData(companyId.inboxIntelCompanyId, true);
              }
            });
            await setTimeout(() => {
              this.getRodData();
            }, 1500);
            if (this.humanVerifiedCompanies.length === 0) {
              this.onClose();
            }
            const allMVCompany = machineCompanyIds.concat(unmatchedCompanyIds);
            this.requestBodyService.changeCompanyRevealMatchesID({ 'type': 'exportCSV', 'data': allMVCompany });
          }
        });
      } else {
        this.toastr.warning('You already requested selected companies.');
        if (this.humanVerifiedCompanies.length === 0) {
          this.onClose();
        }
      }
    } else {
      this.commonUtilService.displayExhaustedRodMessage();
      this.onClose();
    }
  }

  getRodData() {
    this.rodsService.getRods().subscribe((response) => {
      const rodData = response;
      const latestRodData = rodData.map((data) => data?.id).reduce((a, b) => (a + b));
      const lastRodData = this.rodsService.rodRowData.map((data) => data?.id).reduce((a, b) => (a + b));

      const latestRodDataId = latestRodData - lastRodData;
      const rowToAddInExistingData = rodData.find((data) => data?.id === latestRodDataId);

      this.rodsService.rodRowData.unshift(rowToAddInExistingData);
      this.rodsService.setRoDs(this.rodsService.rodRowData);
      this.toastr.success('Requested successfully!');
    });
  }

  exportCompanies(requestBody): Observable<any[]> {
    return this.featureFlagService.getFlag(FeatureFlagsEnum.WebsocketsSf).pipe(
      take(1),
      switchMap((enabled: boolean) => {
        if (enabled) {
          return this.socketExportService.export('salesforceCompaniesExport', requestBody) as Observable<any[]>;
        }

        return this.salesforceApiService.exportCompanies(requestBody) as Observable<any[]>;
      })
    )
  }
}
